import React, { useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  Link,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../shared/hooks/sharedHooks";
import { SubmitHandler, useForm } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import MachineLogoIcon from "../../shared/assets/icons/MachineLogoIcon";
import colors from "../../shared/themes/colors";
import { emailRegex } from "../../shared/validators";
import { Register } from "../../customer/shared/models/customerModels";
import FormNotificationMessage from "../../shared/components/FormNotificationMessage";
import Loader from "../../shared/components/Loader";
import EmailSentConfirmation from "./EmailSentConfirmation";
import { startRegistration, resetState } from "../startRegistrationSlice";
import useTranslation from "../../shared/hooks/useTranslation";
import { useNavigate } from "react-router-dom";
import { AccountRoutePaths } from "../routes";
import { dt } from "../../shared/themes/redesignTokens";

interface StartRegistrationDialogProps {
  open: boolean;
  onToLogin?: () => void;
  onClose: () => void;
}

const StartRegistrationDialog = ({ open, onToLogin, onClose }: StartRegistrationDialogProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { translate } = useTranslation("account");
  const navigate = useNavigate();

  const { isLoading, isConfirmEmail, formNotificationMessage, email } = useAppSelector(
    (state) => state.startRegistration
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Register>();

  const onSubmit: SubmitHandler<Register> = (model: Register) => {
    dispatch(startRegistration(model));
    navigate(AccountRoutePaths.signUp, { state: { email: model.userName } });
  };

  useEffect(() => {
    if (!open) {
      dispatch(resetState());
    }
  }, [open, dispatch]);

  if (isConfirmEmail) {
    return (
      <EmailSentConfirmation
        title={translate("confirmEmailTitle")}
        description={
          <>
            {translate("confirmEmailDescriptionStart")}
            <Box component={"span"} sx={{ fontWeight: 700 }}>{email}</Box>
            {translate("confirmEmailDescriptionEnd")}
          </>
        }
        open={open}
        onClose={onClose}
        hasLogo
      />
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={false}
      hideBackdrop={isMobile}
      PaperProps={{
        sx: {
          ...(isMobile ? {
            margin: 0,
            borderRadius: 0,
            maxHeight: "100vh",
            height: "100vh",
            width: "100vw",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            padding: "32px",
          } : {
            maxWidth: "600px",
            padding: "64px",
            borderRadius: "8px",
          })
        }
      }}
    >
      {isLoading && (
        <Box sx={{
          ...(isMobile ? {
            width: '100%',
            height: '100vh',
          } : {
            width: '460px',
            minHeight: '420px',
          }),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Loader />
        </Box>
      )}

      {!isLoading && (
        <Box
          sx={{
            ...(isMobile && {
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            })
          }}
        >
          <DialogTitle
            className="flex flex-col items-center text-center"
            sx={{
              ...(isMobile && {
                flex: "0 0 auto"
              })
            }}
          >
            <Box
              className="flex flex-col items-center justify-center"
            >
              <MachineLogoIcon />
              <Box
                sx={{
                  color: colors.primary.violetDark,
                  fontSize: "36px",
                  lineHeight: "42px",
                  letterSpacing: "-0.072px",
                  fontWeight: 500,
                  textAlign: "center",
                  paddingX: "40px",
                  ...(isMobile && {
                    fontSize: "28px",
                    lineHeight: "34px",
                    marginTop: "16px",
                    textAlign: "center",
                    paddingX: "20px"
                  })
                }}
              >
                {translate("signUpToAccess")}
              </Box>
            </Box>
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: "absolute",
                right: isMobile ? 16 : 14,
                top: isMobile ? 16 : 14,
                color: colors.secondary.gray20,
                ...(isMobile && {
                  padding: "12px",
                  '& .MuiSvgIcon-root': {
                    fontSize: '24px'
                  }
                })
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              p: 0,
              ...(isMobile && {
                flex: "1 1 auto",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                padding: "18px 8px"
              })
            }}
          >
            {formNotificationMessage && (
              <Box className="mb-3 mt-4">
                <FormNotificationMessage {...formNotificationMessage} />
              </Box>
            )}
            <Box
              component="form"
              id="startRegistrationForm"
              onSubmit={handleSubmit(onSubmit)}
              sx={{ mt: 4 }}
              noValidate>
              <InputLabel>{translate("yourEmail")}</InputLabel>
              <TextField
                fullWidth
                type="email"
                variant="outlined"
                placeholder="mail@mail.com"
                {...register("userName", { required: true, pattern: emailRegex })}
                error={!!errors.userName}
                helperText={errors.userName ? translate("invalidEmail") : ""}
              />
            </Box>

            <Box
              sx={{
                ...(isMobile && {
                  flex: "0 0 auto",
                  marginTop: "auto",
                  paddingTop: "20px",
                  paddingBottom: "20px"
                })
              }}
            >
              <Box sx={{ width: "100%", mt: 6 }}>
                <Button fullWidth form="startRegistrationForm" type="submit" variant="contained">
                  {translate("signUpWithEmail")}
                </Button>
              </Box>

              <Box className="flex flex-col gap-12" sx={{ mt: 3 }}>
                <Box className="text-16-regular" sx={{ display: "inline", color: colors.secondary.gray20 }}>
                  {translate("byContinuing")}
                  <Link href="/terms" target="_blank" sx={{ textDecoration: "underline", textTransform: "capitalize" }}>
                    {translate("termsOfService")}
                  </Link>
                  {translate("andAcknowledge")}
                  <Link href="/privacy" target="_blank" sx={{ textDecoration: "underline", textTransform: "capitalize" }}>
                    {translate("privacyPolicy")}
                  </Link>
                </Box>
                <Box className="flex items-center justify-center">
                  <Box component={"span"} className="text-16-regular" sx={{ color: colors.secondary.gray20 }}>{translate("alreadyMember")}</Box>
                  <Button
                    variant="text"
                    onClick={onToLogin}
                  >
                    <Box className="text-16-regular" sx={{ textDecoration: "underline", color: dt.blue }}>
                      {translate("logIn")}
                    </Box>
                  </Button>
                </Box>
              </Box>
            </Box>
          </DialogContent>
        </Box>
      )}
    </Dialog>
  );
};

export default StartRegistrationDialog;